import { App, Button, Drawer } from 'antd';
import { useTranslation } from 'translations/hooks';
import { db, type Segment } from 'technical/db';
import { SegmentStatus } from 'business/receipt/constants';
import { SynchronizationTag } from 'business/tunnel/pages/ring-scan/components/synchronization-tag';
import dayjs from 'dayjs';

interface Props {
  segment: Segment | null;
  onClose: () => void;
}

export const DeleteSegmentDrawer = ({ segment, onClose }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const handleTunnelReject = async () => {
    if (!segment) {
      return;
    }

    await db.segments.update(segment.serialNumber, {
      ringId: undefined,
      virtualRingNumber: undefined,
      status: SegmentStatus.TunnelRejected,
      desynchronizedAt: dayjs().toISOString(),
    });

    message.success(t('tunnel.scanner.rejectSuccess'));
    onClose();
  };

  const handleDelete = async () => {
    if (!segment) {
      return;
    }
    await db.segments.update(segment.serialNumber, {
      ringId: undefined,
      virtualRingNumber: undefined,
      status: SegmentStatus.Uninstalled,
      desynchronizedAt: dayjs().toISOString(),
    });
    onClose();
  };

  return (
    <Drawer open={segment !== null} placement="bottom" onClose={onClose}>
      <div className="flex h-full flex-col gap-4">
        {segment !== null && (
          <div className="rounded-lg border p-3">
            <div className="grid grid-cols-[1fr_2.5rem_auto] items-center gap-2">
              <span className="truncate font-medium">
                {segment.serialNumber}
              </span>
              <span className="truncate text-gray-500">
                {segment.segmentType}
              </span>
              <SynchronizationTag
                isSynced={segment.desynchronizedAt === null}
              />
            </div>
          </div>
        )}

        <Button
          danger
          type="primary"
          className="h-11 w-full"
          onClick={handleTunnelReject}
        >
          {t('tunnel.segmentList.deleteSegmentDrawer.tunnelRejectButton')}
        </Button>

        <Button
          color="primary"
          variant="outlined"
          className="h-11 w-full"
          onClick={handleDelete}
        >
          {t('tunnel.segmentList.deleteSegmentDrawer.deleteButton')}
        </Button>

        <Button
          color="primary"
          variant="outlined"
          className="mt-auto h-11 w-full"
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </Drawer>
  );
};
